import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { Printer, Bluetooth, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, getVehicleTypeLabel } from '../../lib/utils';
import { isBluetoothSupported, printTicketBluetooth } from '../../lib/bluetoothPrinter';

export default function TicketModal({ open, onOpenChange, ticketData }) {
  const [btLoading, setBtLoading] = useState(false);

  const handlePrintBrowser = () => {
    // Print only the ticket area using a dedicated CSS class
    window.print();
  };

  const handlePrintBluetooth = async () => {
    if (!ticketData) return;
    setBtLoading(true);
    try {
      const payload = {
        folio: ticketData.folio,
        date: ticketData.date,
        client: ticketData.client,
        walk_in: ticketData.walk_in,
        vehicle: ticketData.vehicle
          ? {
              type: getVehicleTypeLabel(ticketData.vehicle.type),
              model: ticketData.vehicle.model,
              plates: ticketData.vehicle.plates,
            }
          : null,
        services: ticketData.services,
        total: ticketData.total,
        payment_method: ticketData.payment_method,
        points_earned: ticketData.points_earned,
        points_redeemed: ticketData.points_redeemed,
      };
      const device = await printTicketBluetooth(payload);
      toast.success(`Impresión enviada (${device})`);
    } catch (err) {
      toast.error(err?.message || 'No se pudo imprimir por Bluetooth');
    }
    setBtLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <div className="ticket-print ticket text-center">
          <div className="mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_carwash-quick-pos/artifacts/tady4vql_image.png"
              alt="WD Logo"
              className="w-16 h-16 mx-auto"
            />
            <h2 className="text-xl font-bold mt-2">WD Lavado Automotriz</h2>
          </div>

          <div className="border-t border-dashed border-slate-300 pt-4 mb-4">
            <div className="text-2xl font-bold text-blue-600 font-mono">{ticketData?.folio}</div>
            <div className="text-sm text-slate-500 mt-1">{ticketData?.date}</div>
          </div>

          <div className="text-left space-y-2 mb-4">
            <div className="flex justify-between">
              <span className="text-slate-500">Cliente:</span>
              <span className="font-medium">
                {ticketData?.client?.name || (ticketData?.walk_in ? 'Walk-in' : '—')}
              </span>
            </div>
            {ticketData?.client?.code && (
              <div className="flex justify-between">
                <span className="text-slate-500">Código:</span>
                <span className="font-mono font-bold text-blue-700">{ticketData.client.code}</span>
              </div>
            )}
            {ticketData?.vehicle && !ticketData?.multi_vehicle && (
              <div className="flex justify-between">
                <span className="text-slate-500">Vehículo:</span>
                <span className="font-medium">
                  {getVehicleTypeLabel(ticketData.vehicle.type)}
                  {ticketData.vehicle.color ? ` · ${ticketData.vehicle.color}` : ''}
                  {ticketData.vehicle.model ? ` · ${ticketData.vehicle.model}` : ''}
                </span>
              </div>
            )}
            {ticketData?.multi_vehicle && (
              <div className="text-xs text-slate-500 text-center italic">Ticket con varios vehículos</div>
            )}
          </div>

          <div className="border-t border-dashed border-slate-300 py-4">
            {(() => {
              const services = ticketData?.services || [];
              if (!ticketData?.multi_vehicle) {
                return services.map((s, i) => (
                  <div key={i} className="flex justify-between text-sm py-1">
                    <span>{s.name}</span>
                    <span className="font-mono">{formatCurrency(s.price)}</span>
                  </div>
                ));
              }
              // Group by vehicle_label
              const groups = {};
              const orderKeys = [];
              services.forEach((s) => {
                const k = s.vehicle_label || 'Vehículo';
                if (!groups[k]) { groups[k] = []; orderKeys.push(k); }
                groups[k].push(s);
              });
              return orderKeys.map((label, gi) => {
                const sub = groups[label].reduce((a, b) => a + b.price, 0);
                return (
                  <div key={gi} className="mb-3 last:mb-0">
                    <div className="text-xs font-bold uppercase text-slate-700 border-b border-dashed border-slate-300 pb-1 mb-1">
                      {gi + 1}. {label}
                    </div>
                    {groups[label].map((s, si) => (
                      <div key={si} className="flex justify-between text-sm py-0.5">
                        <span className="pl-2">{s.name}</span>
                        <span className="font-mono">{formatCurrency(s.price)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs text-slate-500 pt-1">
                      <span className="pl-2">Subtotal</span>
                      <span className="font-mono">{formatCurrency(sub)}</span>
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          <div className="border-t border-dashed border-slate-300 pt-4">
            <div className="flex justify-between text-xl font-bold">
              <span>Total</span>
              <span className="text-blue-600">{formatCurrency(ticketData?.total || 0)}</span>
            </div>
            <div className="text-sm text-slate-500 mt-1 capitalize">
              Pago: {ticketData?.payment_method}
            </div>
          </div>

          {(ticketData?.points_earned > 0 || ticketData?.points_redeemed > 0) && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg">
              {ticketData?.points_earned > 0 && (
                <div className="text-amber-700 text-sm">
                  +{ticketData.points_earned} puntos ganados
                </div>
              )}
              {ticketData?.points_redeemed > 0 && (
                <div className="text-amber-700 text-sm">
                  -{ticketData.points_redeemed} puntos usados
                </div>
              )}
            </div>
          )}

          <div className="mt-4 text-xs text-slate-400">¡Gracias por su preferencia!</div>
        </div>

        <div className="no-print mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={handlePrintBrowser}
            data-testid="print-browser-btn"
          >
            <Printer className="w-4 h-4" />
            Imprimir
          </Button>
          <Button
            variant="outline"
            className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={handlePrintBluetooth}
            disabled={btLoading || !isBluetoothSupported()}
            title={!isBluetoothSupported() ? 'Web Bluetooth no soportado en este navegador' : ''}
            data-testid="print-bluetooth-btn"
          >
            {btLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Bluetooth className="w-4 h-4" />
            )}
            Bluetooth
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="close-ticket-btn">
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
